import { ArrowLeft, Droplets, Info } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { StudyShell } from './StudyShell';
import { toolsHubPath } from './studyTools';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';
import {
  WATER_CHEMISTRY_START_YEAR,
  WATER_CHEMISTRY_TOOL_IDENTITY,
  WATER_MODE_YEARS,
  WATER_MODES,
  WATER_PROPERTY_BOUNDARIES,
  WATER_PROPERTY_CONDITIONS,
  WATER_PROPERTY_SOURCES,
  type WaterMode,
  type WaterSource,
} from './waterChemistry';
import { WaterChemistryProperties } from './WaterChemistryProperties';
import { WaterChemistryPropertiesExplore } from './WaterChemistryPropertiesExplore';
import { WaterChemistryQualityExplore } from './WaterChemistryQualityExplore';
import { WaterChemistryQuality } from './WaterChemistryQualityMode';
import { QUALITY_SOURCES, WATER_QUALITY_BOUNDARIES } from './waterChemistryQuality';

type WaterView = 'explore' | 'practice';

const VIEW_LABELS: Record<WaterView, string> = { explore: 'Explore', practice: 'Practice' };

function modeContent(mode: WaterMode, view: WaterView) {
  if (mode === 'properties') return view === 'explore' ? <WaterChemistryPropertiesExplore /> : <WaterChemistryProperties />;
  return view === 'explore' ? <WaterChemistryQualityExplore /> : <WaterChemistryQuality />;
}

const MODE_COPY: Record<WaterMode, { tab: string; eyebrow: string; title: string; description: string; explore: string }> = {
  properties: {
    tab: 'Properties of water',
    eyebrow: 'Hydrogen bonding in action',
    title: 'Why water is unusual',
    description:
      'Predict how water heats, boils and freezes compared with similar substances, then explain the data with hydrogen bonding.',
    explore:
      'Heat your own sample of water and see every step of the calculation, then compare it with other substances given the same energy.',
  },
  quality: {
    tab: 'Water quality',
    eyebrow: 'Australian Drinking Water Guidelines',
    title: 'Is this water fit to drink?',
    description:
      'Calculate hardness, then compare pH, dissolved solids, hardness and selected ions with ADWG guideline values.',
    explore:
      'Enter calcium, magnesium and dissolved-oxygen results and watch the hardness calculation and guideline comparison update.',
  },
};

export function WaterChemistryLabPage() {
  const year = useChemistryToolYear();
  const [mode, setMode] = useState<WaterMode>(() => modeForYear(WATER_MODE_YEARS, year));
  const [view, setView] = useState<WaterView>('explore');
  const copy = MODE_COPY[mode];
  const sources: WaterSource[] =
    mode === 'properties'
      ? WATER_PROPERTY_SOURCES
      : QUALITY_SOURCES.map((source) => ({ label: source.label, detail: source.detail }));
  const boundaries = mode === 'properties' ? WATER_PROPERTY_BOUNDARIES : WATER_QUALITY_BOUNDARIES;

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'water-chemistry', state: { mode } }}>
      <Link
        to={toolsHubPath('chemistry')}
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 flex flex-col gap-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-mint p-6 shadow-card-soft lg:flex-row lg:items-end lg:justify-between lg:p-8"
        data-tool-key={WATER_CHEMISTRY_TOOL_IDENTITY.toolKey}
        data-tool-version={WATER_CHEMISTRY_TOOL_IDENTITY.toolVersion}
      >
        <div className="max-w-3xl">
          <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
            <Droplets className="h-4 w-4" aria-hidden="true" /> Water &amp; materials
          </p>
          <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Water Chemistry Lab</h1>
          <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
            Explain water’s high specific heat capacity, high enthalpy of vaporisation and density maximum, then judge
            illustrative water samples against Australian guideline values.
          </p>
        </div>
        <div className="rounded-2xl border border-white/80 bg-white/75 px-4 py-3 text-sm font-bold text-ink-soft">
          Year {WATER_CHEMISTRY_START_YEAR}–12 · explore only
        </div>
      </header>

      {year !== null && year < WATER_CHEMISTRY_START_YEAR ? (
        <p role="note" className="mt-4 rounded-2xl border border-brand-sunshine/50 bg-wash-sunshine px-5 py-3 text-sm font-medium text-ink-soft">
          This tool starts at Year {WATER_CHEMISTRY_START_YEAR}. You are viewing Year {year}; you can still explore it.
        </p>
      ) : null}

      <div
        className="mt-6 flex max-w-full flex-wrap gap-1 rounded-[1.5rem] border border-hairline bg-canvas-pure p-1 shadow-card-soft sm:inline-flex sm:rounded-full"
        role="tablist"
        aria-label="Water chemistry mode"
      >
        {WATER_MODES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            role="tab"
            aria-selected={mode === candidate}
            onClick={() => setMode(candidate)}
            className={`rounded-full px-4 py-2.5 text-sm font-extrabold ${mode === candidate ? 'bg-ink text-white' : 'text-slate2'}`}
          >
            {MODE_COPY[candidate].tab}
          </button>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">{copy.eyebrow}</p>
          <h2 className="mt-1 text-2xl font-extrabold text-ink">{copy.title}</h2>
          <p className="mt-1 font-medium text-slate2">{view === 'explore' ? copy.explore : copy.description}</p>
        </div>
        <div className="inline-flex shrink-0 self-start rounded-full border border-hairline bg-canvas-pure p-1" role="group" aria-label="Explore or practise">
          {(Object.keys(VIEW_LABELS) as WaterView[]).map((candidate) => (
            <button
              key={candidate}
              type="button"
              aria-pressed={view === candidate}
              onClick={() => setView(candidate)}
              className={`rounded-full px-4 py-2 text-sm font-extrabold ${view === candidate ? 'bg-brand-sky text-white' : 'text-slate2'}`}
            >
              {VIEW_LABELS[candidate]}
            </button>
          ))}
        </div>
      </div>

      <div key={`${mode}-${view}`} className="mt-5">
        {modeContent(mode, view)}
      </div>

      <section className="my-6 rounded-[1.5rem] border border-hairline bg-surface p-5 text-sm font-medium leading-6 text-slate2">
        <p className="flex items-center gap-2 font-extrabold text-ink">
          <Info className="h-4 w-4" aria-hidden="true" /> Model boundaries and sources
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {mode === 'properties' ? <li>{WATER_PROPERTY_CONDITIONS}</li> : null}
          {boundaries.map((line) => (
            <li key={line}>{line}</li>
          ))}
          <li>
            Progress is not saved, mastery is not recorded and no state syllabus alignment is claimed. Values are
            pending teaching review.
          </li>
        </ul>
        <p className="mt-3">Sources: {sources.map((source) => `${source.label} — ${source.detail}`).join(' ')}</p>
      </section>
    </StudyShell>
  );
}
